import { FontAwesome6 } from '@expo/vector-icons';
import { Link, Stack, type Href, useLocalSearchParams, useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
    Platform,
    ScrollView,
    Text,
    View,
} from 'react-native';
import { useAuth } from '../../lib/AuthContext';
import colors from '../../lib/colors';
import {
    getCheckoutAttemptResult,
    getCheckoutAttemptResultBySession,
    recordCheckoutCancelReturn,
} from '../../lib/checkoutLifecycle';
import type { CheckoutAttemptResult } from '../../lib/checkoutLifecycleContract';
import {
    buildCheckoutRetryHref,
    parseLegacyCheckoutSessionId,
    resolveCheckoutTerminalSyncState,
    type CheckoutResultMode,
} from '../../lib/checkoutResultContract';
import { buildCanonicalDashboardPath } from '../../lib/dashboardRoutes';
import { clearDigestQueryCache } from '../../lib/digestQueryCache';
import { refreshCurrentEntitlementsAuthoritatively } from '../../lib/hooks/useCurrentEntitlements';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interMedium, interRegular, interSemiBold } from '../../lib/fonts';
import { useSession } from '../../lib/SessionContext';
import Footer from '../Footer';
import Header from '../Header';
import MetaTags from '../MetaTags';
import Button from '../ui/Button';
import {
    checkoutStatusCopy,
    type CheckoutSyncState,
} from './checkoutResultCopy';
import { checkoutResultStyles as styles } from './checkoutResultStyles';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useOptionalResolvedMessage, useResolvedMessage } from '../../lib/i18n/messages';

const MAX_SYNC_ATTEMPTS = 8;
const CANCEL_SYNC_ATTEMPTS = 4;
const SYNC_RETRY_MS = 2000;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function toSingleParam(value?: string | string[]): string | undefined {
    return Array.isArray(value) ? value[0] : value;
}

function safeUuid(value?: string): string | undefined {
    return value && uuidPattern.test(value) ? value.toLowerCase() : undefined;
}

function safeBilling(value?: string): 'monthly' | 'yearly' | undefined {
    return value === 'monthly' || value === 'yearly' ? value : undefined;
}

function shortAttemptId(value?: string): string | null {
    return value ? `${value.slice(0, 8)}...${value.slice(-4)}` : null;
}

export default function CheckoutResultScreen({ mode }: { mode: CheckoutResultMode }) {
    const {
        checkout_attempt_id,
        session_id,
        preview_project_id,
        result_digest_id,
        billing: billingParam,
        lookup_key,
        currency: currencyParam,
    } = useLocalSearchParams<{
        checkout_attempt_id?: string | string[];
        session_id?: string | string[];
        preview_project_id?: string | string[];
        result_digest_id?: string | string[];
        billing?: string | string[];
        lookup_key?: string | string[];
        currency?: string | string[];
    }>();
    const router = useRouter();
    const queryClient = useQueryClient();
    const { openAuth } = useAuth();
    const { session, isLoading } = useSession();
    const { fontsLoaded } = useFontLoading();
    const [syncState, setSyncState] = useState<CheckoutSyncState>(
        mode === 'success' ? 'checking' : 'left',
    );
    const [attemptResult, setAttemptResult] = useState<CheckoutAttemptResult | null>(null);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [retryNonce, setRetryNonce] = useState(0);
    const attemptsRef = useRef(0);
    const redirectStartedRef = useRef(false);
    const cancelSignalAttemptRef = useRef<string | null>(null);

    const checkoutAttemptId = safeUuid(toSingleParam(checkout_attempt_id));
    const legacyCheckoutSessionId = parseLegacyCheckoutSessionId(toSingleParam(session_id));
    const requestedProjectId = safeUuid(toSingleParam(preview_project_id));
    const requestedDigestId = safeUuid(toSingleParam(result_digest_id));
    const billing = safeBilling(toSingleParam(billingParam));
    const requestedPlanLookupKey = toSingleParam(lookup_key);
    const requestedCurrency = toSingleParam(currencyParam);

    useEffect(() => {
        attemptsRef.current = 0;
        redirectStartedRef.current = false;
        setErrorText(null);
        setAttemptResult(null);
        setSyncState(mode === 'success' ? 'checking' : 'left');

        if (isLoading) {
            return;
        }

        if (!session?.user?.id) {
            setSyncState('signed_out');
            return;
        }

        if (!checkoutAttemptId && !legacyCheckoutSessionId) {
            setSyncState(mode === 'success' ? 'error' : 'left');
            if (mode === 'success') {
                setErrorText('The checkout reference is missing from this return URL.');
            }
            return;
        }

        let isCancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        const maxAttempts = mode === 'cancelled' ? CANCEL_SYNC_ATTEMPTS : MAX_SYNC_ATTEMPTS;

        const scheduleRetry = (lastError?: Error, completed = false) => {
            if (isCancelled) return;
            if (attemptsRef.current >= maxAttempts) {
                if (lastError) setErrorText(lastError.message || 'Unknown error');
                setSyncState(resolveCheckoutTerminalSyncState({
                    mode,
                    hadError: Boolean(lastError),
                    completed,
                }));
                return;
            }
            timeoutId = setTimeout(checkAttempt, SYNC_RETRY_MS);
        };

        const publishCompletedAccess = async (result: CheckoutAttemptResult): Promise<boolean> => {
            if (result.trialStatus !== 'trialing' && result.trialStatus !== 'active') return false;
            const snapshot = await refreshCurrentEntitlementsAuthoritatively(queryClient);
            if (isCancelled) return false;

            const projectId = result.projectId ?? requestedProjectId;
            const digestId = result.resultDigestId ?? requestedDigestId;
            if (projectId) {
                const preview = snapshot.billing_preview;
                const converted = preview?.status === 'converted'
                    && preview.project_id === projectId
                    && (!digestId || preview.result_digest_id === digestId);
                if (!converted) return false;
            }

            await clearDigestQueryCache(queryClient);
            return !isCancelled;
        };

        const checkAttempt = async () => {
            attemptsRef.current += 1;
            if (mode === 'success') setSyncState('checking');
            let completedAttempt = false;

            try {
                const result = checkoutAttemptId
                    ? await getCheckoutAttemptResult(checkoutAttemptId)
                    : await getCheckoutAttemptResultBySession(legacyCheckoutSessionId as string);
                if (isCancelled) return;
                if (
                    (checkoutAttemptId && result.attemptId !== checkoutAttemptId)
                    || (legacyCheckoutSessionId && result.checkoutSessionId !== legacyCheckoutSessionId)
                    || (requestedProjectId && result.projectId !== requestedProjectId)
                    || (requestedDigestId && result.resultDigestId !== requestedDigestId)
                ) {
                    throw new Error('Checkout result did not match the requested project context.');
                }
                completedAttempt = result.completed;
                setAttemptResult(result);

                if (
                    mode === 'cancelled'
                    && cancelSignalAttemptRef.current !== result.attemptId
                ) {
                    cancelSignalAttemptRef.current = result.attemptId;
                    void recordCheckoutCancelReturn(result.attemptId).catch((error) => {
                        cancelSignalAttemptRef.current = null;
                        console.warn('Could not record weak Checkout return context:', error);
                    });
                }

                if (!result.completed) {
                    if (mode === 'success' && (result.state === 'expired' || result.state === 'superseded')) {
                        setSyncState('closed');
                        return;
                    }
                    if (mode === 'cancelled') setSyncState('left');
                    scheduleRetry();
                    return;
                }

                if (!await publishCompletedAccess(result)) {
                    setSyncState('pending');
                    scheduleRetry(undefined, true);
                    return;
                }

                setSyncState('ready');
                if (!redirectStartedRef.current && result.destination) {
                    redirectStartedRef.current = true;
                    timeoutId = setTimeout(() => {
                        router.replace(result.destination as Href);
                    }, 350);
                }
            } catch (error) {
                if (isCancelled) return;
                const normalizedError = error instanceof Error
                    ? error
                    : new Error('Unknown checkout error');
                console.error('Exact checkout attempt check failed:', normalizedError);
                scheduleRetry(normalizedError, completedAttempt);
            }
        };

        void checkAttempt();

        return () => {
            isCancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [
        checkoutAttemptId,
        isLoading,
        legacyCheckoutSessionId,
        mode,
        queryClient,
        requestedDigestId,
        requestedProjectId,
        retryNonce,
        router,
        session?.user?.id,
    ]);

    const projectId = attemptResult?.projectId ?? requestedProjectId;
    const destinationDigestId = attemptResult?.resultDigestId ?? requestedDigestId;
    const resolvedCheckoutAttemptId = attemptResult?.attemptId ?? checkoutAttemptId;
    const authoritativeSuccess = Boolean(attemptResult?.completed);
    const presentationMode = authoritativeSuccess ? 'success' : mode;
    const copy = checkoutStatusCopy(
        syncState,
        presentationMode,
        attemptResult,
        Boolean(projectId),
        Boolean(legacyCheckoutSessionId),
    );
    const copyEyebrow = useResolvedMessage(copy.eyebrow);
    const copyTitle = useResolvedMessage(copy.title);
    const copyBody = useResolvedMessage(copy.body);
    const copyFinePrint = useOptionalResolvedMessage(copy.finePrint);
    const iconName = syncState === 'ready'
        ? 'circle-check'
        : mode === 'cancelled' && !authoritativeSuccess ? 'arrow-right-from-bracket' : 'clock';
    const iconColor = syncState === 'ready'
        ? colors.primaryGreen
        : mode === 'cancelled' && !authoritativeSuccess ? colors.text600 : colors.action;
    const destinationHref = attemptResult?.destination
        ?? (destinationDigestId
            ? `/digest/${destinationDigestId}`
            : projectId
                ? buildCanonicalDashboardPath(`/${projectId}`)
                : buildCanonicalDashboardPath());
    const retryHref = buildCheckoutRetryHref({
        result: attemptResult,
        requestedPlanLookupKey,
        requestedCurrency,
        billing,
        requestedPreviewProjectId: requestedProjectId,
        requestedPreviewResultDigestId: requestedDigestId,
    }) as Href;
    const returnParams = new URLSearchParams();
    if (checkoutAttemptId) returnParams.set('checkout_attempt_id', checkoutAttemptId);
    if (legacyCheckoutSessionId) returnParams.set('session_id', legacyCheckoutSessionId);
    if (requestedProjectId) returnParams.set('preview_project_id', requestedProjectId);
    if (requestedDigestId) returnParams.set('result_digest_id', requestedDigestId);
    if (billing) returnParams.set('billing', billing);
    if (requestedPlanLookupKey) returnParams.set('lookup_key', requestedPlanLookupKey);
    if (requestedCurrency) returnParams.set('currency', requestedCurrency);
    const checkoutReturnHref = `/checkout/${mode === 'cancelled' ? 'cancelled' : 'success'}${
        returnParams.size ? `?${returnParams.toString()}` : ''
    }`;
    const shouldContinueCheckout = !authoritativeSuccess
        && syncState !== 'signed_out'
        && (
            (mode === 'cancelled' && syncState === 'left')
            || syncState === 'closed'
        );
    const showDestinationAction = syncState === 'ready' || shouldContinueCheckout;
    const showSignInAction = syncState === 'signed_out';
    const showRetryAction = Boolean(checkoutAttemptId || legacyCheckoutSessionId)
        && (syncState === 'pending' || syncState === 'error');

    return (
        <>
            <MetaTags
                surfaceId={mode === 'cancelled' ? 'checkout-cancelled' : 'checkout-success'}
                title={presentationMode === 'cancelled'
                    ? msg({
                        id: 'seo.checkout.cancelledTitle',
                        message: 'Checkout left | Paperzilla',
                        comment: 'Cancelled-checkout browser, Open Graph, and Twitter title.',
                    })
                    : msg({
                        id: 'seo.checkout.statusTitle',
                        message: 'Checkout status | Paperzilla',
                        comment: 'Checkout-status browser, Open Graph, and Twitter title.',
                    })}
                description={copyBody}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.container}>
                <Header hidePreviewTrialBanner />
                <ScrollView
                    style={styles.scrollView}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.resultSection}>
                        <View style={styles.card}>
                            <View style={[styles.iconWrap, { borderColor: iconColor }]}>
                                <FontAwesome6 name={iconName} size={30} color={iconColor} />
                            </View>
                            <View
                                style={styles.statusCopy}
                                accessibilityLiveRegion="polite"
                                testID="checkout-sync-status"
                            >
                                <Text style={[styles.eyebrow, fontsLoaded && interSemiBold]}>
                                    {copyEyebrow}
                                </Text>
                                <Text
                                    style={[styles.title, fontsLoaded && fonts.displayMedium]}
                                    accessibilityRole="header"
                                    {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                                >
                                    {copyTitle}
                                </Text>
                                <Text style={[styles.body, fontsLoaded && interRegular]}>
                                    {copyBody}
                                </Text>

                                {copyFinePrint && (
                                    <Text style={[styles.finePrint, fontsLoaded && interRegular]}>
                                        {copyFinePrint}
                                    </Text>
                                )}
                            </View>

                            {resolvedCheckoutAttemptId && (
                                <View style={styles.sessionPill}>
                                    <Text style={[styles.sessionLabel, fontsLoaded && interMedium]}><Trans id="checkout.checkoutresultscreen.checkoutAttempt" comment="UI copy in components/checkout/CheckoutResultScreen.tsx">
                                        Checkout attempt
                                    </Trans></Text>
                                    <Text style={[styles.sessionValue, fontsLoaded && interRegular]}>
                                        {shortAttemptId(resolvedCheckoutAttemptId)}
                                    </Text>
                                </View>
                            )}

                            {errorText && (
                                <Text
                                    style={[styles.errorText, fontsLoaded && interRegular]}
                                    accessibilityRole="alert"
                                >
                                    {errorText}
                                </Text>
                            )}

                            {showSignInAction ? (
                                <View style={styles.actions}>
                                    <Button
                                        label={msg({ id: 'checkout.result.signInAction', message: 'Sign in to check checkout' })}
                                        variant="primary"
                                        onPress={() => openAuth({
                                            mode: 'signin',
                                            redirectTo: checkoutReturnHref,
                                        })}
                                        textStyle={fontsLoaded && interSemiBold}
                                        style={styles.actionButton}
                                    />
                                </View>
                            ) : showDestinationAction ? (
                                <View style={styles.actions}>
                                    <Link
                                        href={shouldContinueCheckout
                                            ? retryHref
                                            : destinationHref}
                                        asChild
                                    >
                                        <Button
                                            label={shouldContinueCheckout
                                                ? msg({ id: 'checkout.result.continue', message: 'Continue checkout' })
                                                : destinationDigestId
                                                    ? msg({ id: 'checkout.result.openDigest', message: 'Open complete digest' })
                                                    : projectId
                                                        ? msg({ id: 'handoff.openPaperFeed', message: 'Open Paper Feed' })
                                                        : msg({ id: 'checkout.result.openDashboard', message: 'Open dashboard' })}
                                            variant="primary"
                                            textStyle={fontsLoaded && interSemiBold}
                                            style={styles.actionButton}
                                        />
                                    </Link>
                                </View>
                            ) : showRetryAction ? (
                                <View style={styles.actions}>
                                    <Button
                                        label={msg({ id: 'checkout.result.checkAgain', message: 'Check this checkout again' })}
                                        variant="secondary"
                                        onPress={() => setRetryNonce((value) => value + 1)}
                                        textStyle={fontsLoaded && interSemiBold}
                                        style={styles.actionButton}
                                    />
                                </View>
                            ) : null}
                            {mode === 'cancelled' && !authoritativeSuccess && (
                                <Text style={[styles.supportText, fontsLoaded && interRegular]}><Trans id="checkout.checkoutresultscreen.needHelpContactHelloPaperzillaAi" comment="UI copy in components/checkout/CheckoutResultScreen.tsx">
                                    Need help? Contact hello@paperzilla.ai.
                                </Trans></Text>
                            )}
                        </View>
                    </View>
                    <Footer />
                </ScrollView>
            </View>
        </>
    );
}
